import {
  DownloadOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Button, Spin } from 'antd'
import React, { useState } from 'react'
import { Document, Page } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import '../../orgChart/orgChart.scss'

// PDF.js worker는 index.js에서 이미 설정됨

const DocumentViewer = ({
  pdfUrl,
  onPageChange,
  downloadFileName,
  onDownload,
}) => {
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [scale, setScale] = useState(1.0)
  const [loading, setLoading] = useState(true)

  const zoomLevels = [0.25, 0.5, 0.75, 1.0, 1.25, 1.5]
  const currentZoomIndex = zoomLevels.findIndex((z) => z === scale)

  const onDocumentLoadSuccess = ({ numPages }) => {
    // console.log('PDF 로드 성공! 페이지 수:', numPages)
    setNumPages(numPages)
    setLoading(false)
  }

  const onDocumentLoadError = (error) => {
    console.error('PDF 로드 오류:', error)
    console.error('PDF URL:', pdfUrl)
    setLoading(false)
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= numPages) {
      setPageNumber(newPage)
      onPageChange?.(newPage)
    }
  }

  const handleZoomIn = () => {
    if (currentZoomIndex < zoomLevels.length - 1) {
      setScale(zoomLevels[currentZoomIndex + 1])
    }
  }

  const handleZoomOut = () => {
    if (currentZoomIndex > 0) {
      setScale(zoomLevels[currentZoomIndex - 1])
    }
  }

  const handleThumbnailClick = (page) => {
    handlePageChange(page)
  }

  const handleDownload = () => {
    if (onDownload) {
      onDownload()
      return
    }
    if (pdfUrl && downloadFileName) {
      const link = document.createElement('a')
      link.href = pdfUrl
      link.download = downloadFileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const showDownload = (downloadFileName || onDownload) && pdfUrl && !loading

  return (
    <div className="document-viewer">
      <div className="document-viewer-main">
        <div
          className="document-viewer-controls"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
            }}
          >
            <Button
              icon={<MinusOutlined />}
              onClick={handleZoomOut}
              disabled={currentZoomIndex === 0}
              size="small"
            />
            <span className="zoom-level">{Math.round(scale * 100)}%</span>
            <Button
              icon={<PlusOutlined />}
              onClick={handleZoomIn}
              disabled={currentZoomIndex === zoomLevels.length - 1}
              size="small"
            />
          </div>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              paddingRight: '40px',
            }}
          >
            {showDownload && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleDownload}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleDownload()
                  }
                }}
                title="다운로드"
                style={{
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '20px',
                  flexShrink: 0,
                }}
              >
                <DownloadOutlined />
              </span>
            )}
          </div>
        </div>

        <div className="document-viewer-content">
          {/* 좌측 썸네일 */}
          <div className="document-thumbnails">
            {numPages &&
              Array.from({ length: numPages }, (_, i) => i + 1).map((page) => (
                <div
                  key={page}
                  className={`thumbnail-item ${
                    page === pageNumber ? 'active' : ''
                  }`}
                  onClick={() => handleThumbnailClick(page)}
                >
                  <Document file={pdfUrl}>
                    <Page pageNumber={page} scale={0.15} />
                  </Document>
                  <div className="thumbnail-label">{page}</div>
                </div>
              ))}
          </div>

          <div className="document-pdf-wrapper">
            <Spin spinning={loading}>
              {pdfUrl ? (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={
                    <div className="document-pdf-loading">PDF 로딩 중...</div>
                  }
                  error={
                    <div className="document-pdf-error">
                      PDF를 불러올 수 없습니다.
                      <br />
                      <small className="document-pdf-error-small">
                        URL: {pdfUrl?.substring(0, 50)}...
                      </small>
                    </div>
                  }
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                </Document>
              ) : (
                <div className="document-pdf-no-url">PDF URL이 없습니다.</div>
              )}
            </Spin>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DocumentViewer
